import { useState, useEffect, useCallback } from "react";
import FilterMenu from "../../../components/FilterMenu";
import Loading from "../../../components/Loading";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../components/recharts/functions";
import {
  ReMixChart,
  lightenColor,
} from "../../../components/recharts/ReMixCharts";

import { RajasthanDiscomData, RajGenProfile } from "../../../models/rajasthan";
import {
  getRajasthanForecastDiscomData,
  getRajGenProfileData,
  getSurDefProfile,
} from "../../../Rest_api/RestApi";
import { ExchangeColors } from "../../Dashboard/Exchange3/FormatData";
import { R_CHART_TYPES } from "../const";
import { FUNCTIONS } from "../const_raj";
import { convertToString } from "../../../extensions/date";
import { renderHourTick } from "../../../components/recharts/components";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";
import MainPageContainer from "../../../components/MainPageContainer";
import SecondaryHeader from "./SecondaryHeader";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import { useSelector } from "react-redux";
import Tooltip from "../../../components/tooltip";
import { stringToConsistentColor } from "../../../extensions/string";
import { AddDatesWithFileName } from "../../../helpers/helper";
import { table } from "console";
import { BrushStart } from "../../../models/chart_model";

// Grouping and filtering configurations
const GROUP_BY = [
  {
    label: "Area",
    key: "area",
    values: ["area", "discom"],
  },
  {
    label: "Discom",
    key: "discom",
    values: ["discom"],
  },
];

const DISCOM = [
  {
    label: "Discom 1",
    key: "DISCOM1",
  },
  {
    label: "Discom 2",
    key: "DISCOM2",
  },
  {
    label: "Discom 3",
    key: "DISCOM3",
  },
];

// Updated GEN_BY using only payload keys and computing deficit from forecasted and total
const GEN_BY = [
  {
    label: "Cumulative",
    keys: [
      {
        key: "deficit",
        label: "Surplus/Deficit",
        stackId: "deficit",
      },
      {
        key: "deficit_percent",
        label: "Surplus/Deficit %",
        yAxis: "right",
        stackId: "deficit",
      },
      // {
      //   key: "total",
      //   label: "Total Gen. + Proc.",
      //   stackId: "gen",
      // },
    ],
  },
  {
    label: "Comparative",
    keys: [
      {
        key: "deficit",
        label: "Surplus/Deficit",
        stackId: "deficit",
      },
      {
        key: "deficit_percent",
        label: "Surplus/Deficit %",
        yAxis: "right",
        stackId: "deficit",
      },
      { key: "sgu", label: "SGU", stackId: "gen" },
      { key: "biomass", label: "Biomass", stackId: "gen" },
      { key: "hydro", label: "Hydro", stackId: "gen" },
      { key: "solar", label: "Solar", stackId: "gen" },
      { key: "wind", label: "Wind", stackId: "gen" },
      { key: "isgs", label: "ISGS", stackId: "gen" },
      { key: "lta", label: "LTA", stackId: "gen" },
      { key: "ldc", label: "LDC", stackId: "gen" },
      {
        key: "kalisindhtpsstage1_unit1",
        label: "kalisindhtpsstage1 unit1",
        stackId: "gen",
      },
      { key: "adanikawai", label: "Adani Kawai", stackId: "gen" },
      { key: "barsingsarneyveli", label: "Barsingsar Neyveli", stackId: "gen" },
      {
        key: "chhabratpsstage1_unit12",
        label: "Chhabra TPS Stage1 Unit1/2",
        stackId: "gen",
      },
      // { key: "creplb_biomass", label: "CREPLB Biomass", stackId: "gen" },
      { key: "csctpp", label: "CSCTPP", stackId: "gen" },
      {
        key: "dholpurgtpp_unit123",
        label: "Dholpur GTPP Unit1/2/3",
        stackId: "gen",
      },
      { key: "giral", label: "Giral", stackId: "gen" },
      // { key: "jgpeplb_biomass", label: "JGPEPLB Biomass", stackId: "gen" },
      { key: "ktpsstage12345", label: "KTPS Stage1/2/3/4/5", stackId: "gen" },
      { key: "rajwest_unit8", label: "Rajwest Unit8", stackId: "gen" },
      {
        key: "ramgarhgtppstage123",
        label: "Ramgarh GTPP Stage1/2/3",
        stackId: "gen",
      },
      { key: "rappa", label: "RAPPA", stackId: "gen" },
      { key: "ssctps_6", label: "SSCTPS 6", stackId: "gen" },
      { key: "stpsstage1234", label: "STPS Stage1/2/3/4", stackId: "gen" },
      { key: "vslignite", label: "VS Lignite", stackId: "gen" },
    ],
  },
];

const DRAWAL_BY = [
  {
    label: "Cumulative",
    keys: [{ key: "forecasted", label: "Total Drawal", stackId: "drawal" }],
  },
  {
    label: "Comparative",
    keys: [{ key: "forecasted", label: "Total Drawal", stackId: "drawal" }],
  },
];

// X_AXIS uses time_block (which exists in payload) and date
const X_AXIS = [
  {
    label: "Time Slot",
    key: "time",
    values: ["time_block", "date"],
    isTime: true,
  },
  {
    label: "Discom/ Area",
    key: "area",
    isTime: false,
    values: ["area", "discom"],
  },
];

const CONTENT_TYPE = ["Chart", "Table"];

const MODELS = ["Model 1", "Model 2"];

export function Rajasthan_Forecast_Surplus() {
  const [TableData, setTableData] = useState<any[]>([]);
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates
  );
  const [data, setData] = useState<RajasthanDiscomData[]>([]);
  const [genData, setGenData] = useState<RajGenProfile[]>([]);
  const [genGroupBy, setGenGroupBy] = useState(GEN_BY[0]);
  const [drawalGroupBy, setDrawalGroupBy] = useState(DRAWAL_BY[0]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [selectedDiscom, setSelectedDiscom] = useState<any[]>([]);
  const [selectedModel, setSelectedModel] = useState(MODELS[0]);
  const [selectedArea, setSelectedArea] = useState<any[]>([]);
  // Note: selectedContentType controls whether we show Chart/Table
  const [selectedContentType, setSelectedContentType] = useState(
    CONTENT_TYPE[0]
  );
  const [uniqueDate, setUniqueDate] = useState<any[]>([]);
  const [selectedDate, setSelectedDate] = useState<any[]>([]);
  // Updated: using time_block (not block) from payload
  const [uniqueBlock, setUniqueBlock] = useState<any[]>([]);
  const [selectedBlock, setSelectedBlock] = useState<any[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [selectedChartType, setSelectedChartType] = useState(R_CHART_TYPES[2]);
  const [selectedGroupKeys, setSelectedGroupKeys] = useState([GROUP_BY[1]]);
  const [selectedXaxis, setSelectedXaxis] = useState(X_AXIS[0]);
  const [loading, setLoading] = useState(true);

  const _handlefetchData = useCallback(async () => {
    if (DefaultDates[activeTab].defaultRangeDate === undefined) {
      return;
    }
    setLoading(true);
    const dates = DefaultDates[activeTab].defaultRangeDate;
    const newData = await getRajasthanForecastDiscomData(
      convertToString(dates.startDate),
      convertToString(dates.endDate),
      selectedModel
    );
    const genData = await getSurDefProfile({
      startDate: convertToString(dates.startDate),
      endDate: convertToString(dates.endDate),
    });
    setData(newData);
    setGenData(genData);

    // Using "date" from payload
    setUniqueDate(
      Array.from(
        new Set(
          newData
            .map((item) => item.date)
            .sort((a, b) => new Date(a).getTime() - new Date(b).getTime())
        )
      )
    );
    // Updated: use "time_block" (since "block" is not in the payload)
    setUniqueBlock(
      Array.from(new Set(newData.map((item: any) => item.time_block)))
    );
    setLoading(false);
  }, [activeTab, DefaultDates, selectedModel]);

  useEffect(() => {
    let filteredData = data;

    if (selectedDiscom.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedDiscom.includes(item.discom)
      );
    }
    if (selectedDate.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedDate.map((d) => d.value).includes(item.date)
      );
    }
    // Updated: filtering by time_block instead of block
    if (selectedBlock.length > 0) {
      filteredData = filteredData.filter((item: any) =>
        selectedBlock.map((d) => d.value).includes(item.time_block)
      );
    }

    // Filter by date range
    const dates = DefaultDates[activeTab].defaultRangeDate;
    filteredData = filteredData.filter(
      (item) =>
        new Date(item.date) >= dates.startDate &&
        new Date(item.date) <= dates.endDate
    );

    // For the drawal/chart data, we now use "forecasted" as the volume key.
    const par: ChartDataParams = {
      keys: selectedXaxis.isTime
        ? selectedBlock.length > 0 && selectedContentType === CONTENT_TYPE[0]
          ? ["date"]
          : selectedXaxis.values
        : selectedGroupKeys[0].values,
      groupBy: selectedXaxis.isTime
        ? selectedGroupKeys
            .map((k) => k.key)
            .concat(
              selectedBlock.length > 0 &&
                selectedContentType === CONTENT_TYPE[0]
                ? ["time_block"]
                : []
            )
        : [],
      tableKeys: ["date", "time_block", "month"],
      volumeKey: "forecasted",
      aggregationKeys: ["forecasted"],
      priceKey: "",
      data: filteredData,
    };
    const cData = FormatChartData(par);

    // Since the payload contains only "forecasted" for drawal, map it directly:
    const drawal = cData.data.map((item) => ({
      ...item,
      Total_drawal_mw: item.forecasted,
    }));

    function cleanOnbardcKeys(obj: Record<string, any>) {
      const cleaned: Record<string, any> = {};
      Object.entries(obj).forEach(([key, value]) => {
        if (key.startsWith("onbardc_")) {
          // Remove only the "onbardc_" prefix
          const newKey = key.replace(/^onbardc_/, "");
          cleaned[newKey] = value;
        } else {
          cleaned[key] = value;
        }
      });

      return cleaned;
    }
    // Usage in your code:
    const final_raw_data = genData.map(cleanOnbardcKeys);

    // Update the aggregation to use only the payload keys:
    const chartPar: ChartDataParams = {
      keys: ["date", "time_block"],
      tableKeys: ["date", "time_block"],
      groupBy: [],
      aggregationKeys: [
        "forecasted",
        "total",
        "sgu",
        "biomass",
        "hydro",
        "solar",
        "wind",
        "isgs",
        "lta",
        "ldc",
        "kalisindhtpsstage1_unit1",
        "adanikawai",
        "barsingsarneyveli",
        "chhabratpsstage1_unit12",
        // "creplb_biomass",
        "csctpp",
        "dholpurgtpp_unit123",
        "giral",
        // "jgpeplb_biomass",
        "ktpsstage12345",
        "rajwest_unit8",
        "ramgarhgtppstage123",
        "rappa",
        "ssctps_6",
        "stpsstage1234",
        "vslignite",
      ],
      priceKey: "",
      volumeKey: "",
      data: final_raw_data,
    };

    const res_data = FormatChartData(chartPar);
    // Compute deficit (as forecasted - total) and its percent
    const final_data = res_data.data.map((item) => {
      return {
        ...item,
        _deficit: item._total - item._forecasted,
        _deficit_percent:
          ((item._total - item._forecasted) / item._forecasted) * 100,
      };
    });

    const finalTableData = res_data.data.map((item) => {
      return {
        ...item,
        _deficit: item._total - item._forecasted,
        _deficit_percent:
          ((item._total - item._forecasted) / item._forecasted) * 100,
      };
    });
    setChartData(final_data);
    setChartKeys(cData.keys);
    setTableData(finalTableData || []);
  }, [
    data,
    selectedGroupKeys,
    selectedXaxis,
    selectedDiscom,
    selectedArea,
    selectedDate,
    selectedBlock,
    selectedChartType,
    selectedContentType,
    genData,
    DefaultDates,
    activeTab,
    setTableData,
  ]);

  const tablePar = {
    legends: (() => {
      const merged = drawalGroupBy.keys.concat(genGroupBy.keys);
      let genCount = 0;
      return merged.map((key) => ({
        dataKey: "_" + key.key,
        name: key.stackId === "gen" ? `Power plant ${++genCount}` : key.label,
      }));
    })(),
    tableData: TableData,
    filename: AddDatesWithFileName({
      fileName: "Forecast Discom Load",
      dates: DefaultDates[activeTab].defaultRangeDate,
    }),
    data: chartData,
    unit: "MW",
    extraKeys: selectedXaxis.isTime
      ? selectedBlock.length < 1
        ? ["time_block", "date"]
        : ["date"]
      : [selectedXaxis.values[0]],
    extraLabels: selectedXaxis.isTime
      ? selectedBlock.length < 1
        ? ["Block", "date"]
        : ["date"]
      : [selectedXaxis.values[0]],
  };

  useEffect(() => {
    _handlefetchData();
  }, [_handlefetchData]);

  return (
    <div className="gas-page-container">
      {loading && <Loading />}
      <NewFilterContainer>
        <div className="side-filters-container text-nowrap">
          <FilterMenu
            title="Generation By"
            items={GEN_BY.map((item) => item.label)}
            selectedItems={[genGroupBy.label]}
            onSelect={(item, index) => {
              setGenGroupBy(GEN_BY[index]);
            }}
          />
          {/* <FilterMenu
          title="Drawal By"
          items={DRAWAL_BY.map((item) => item.label)}
          selectedItems={[drawalGroupBy.label]}
          onSelect={(item, index) => {
            setDrawalGroupBy(DRAWAL_BY[index]);
          }}
        /> */}
          <FilterMenu
            title="Discom"
            items={DISCOM.map((item) => item.label)}
            selectedItems={selectedDiscom}
            onSelect={(item, index) => {}}
            setSelectedItems={setSelectedDiscom}
          />
          <FilterMenu
            title="Chart Type"
            items={R_CHART_TYPES.map((item) => item.label)}
            selectedItems={[selectedChartType.label]}
            onSelect={(item, index) => {
              setSelectedChartType(R_CHART_TYPES[index]);
            }}
          />
        </div>
      </NewFilterContainer>

      <MainPageContainer>
        <SecondaryHeader />
        <div className="gas-chart-container">
          <div className="gas-heading gap-x-10">
            <h2 className="chartHeading w-60">Forecast (MW)</h2>
            <div className="flex gap-x-6  justify-end">
              <button
                onClick={() => {
                  if (dataTab.label === "Chart") {
                    downloadChartTableCSV(tablePar);
                  }
                  if (dataTab.label === "Summary") {
                    downloadSummaryCSV({ ...tablePar });
                  }
                  if (dataTab.label === "Table") {
                    downloadChartTableCSV(tablePar);
                  }
                }}
                className="downloadButton"
              >
                Download Data
              </button>
            </div>
          </div>
          {dataTab.label === "Chart" && (
            <ReMixChart
              data={chartData}
              showBrush={chartData.length > 96 ? true : undefined}
              brushStart={BrushStart.Start}
              isTimeSlot={selectedBlock.length < 1}
              onlyTitle={selectedBlock.length !== 0}
              barCategoryGap={10}
              unit="MW"
              yAxisLabel="MW"
              secondYAxisLabel="%"
              legends={(() => {
                const merged = genGroupBy.keys
                  .concat(drawalGroupBy.keys)
                  .reverse();
                // let genCount = 0;
                return merged.map((key) => ({
                  dataKey: "_" + key.key,
                  // name:
                  //   key.stackId === "gen"
                  //     ? `Power plant ${++genCount}`
                  //     : key.label,
                  name: key.label,
                  legendColor: undefined,
                  stroke:undefined,
                  yAxisId: key.yAxis,
                  type: selectedChartType.value,
                  stackId:
                    selectedChartType.stackId !== undefined
                      ? key.stackId
                      : undefined,
                }));
              })()}
              xDataKey={selectedBlock.length < 1 ? "time_block" : "date"}
              secondXDataKey={
                selectedXaxis.isTime && selectedBlock.length === 0
                  ? selectedXaxis.values[1]
                  : undefined
              }
              xTick={(value: any, index: any) => renderHourTick(value, 10)}
            />
          )}
          {dataTab.label === "Summary" && <LegendSummaryTable {...tablePar} />}
          {dataTab.label === "Table" && <ChartTable {...tablePar} />}
        </div>
        <div className="gna-footer-container">
          <div className="grid grid-cols-2 justify-between gap-5 ">
            <p className="gna-footer-text text-start col-span-1">
              Powered by <strong>GNA Energy</strong>
            </p>
            <strong className="gna-footer-text col-span-1 text-end">
              Demo Version
            </strong>
          </div>
        </div>
      </MainPageContainer>
    </div>
  );
}
